import {
  AuthProvider as OidcAuthProvider,
  useAuth as useOidcAuth,
} from 'react-oidc-context'
import { WebStorageStateStore } from 'oidc-client-ts'
import type { AuthProviderProps } from 'react-oidc-context'

/**
 * Keycloak (OIDC) auth for talva web, replacing @auth0/auth0-react.
 *
 * The Keycloak access token is the bearer sent to Hasura (JWT mode); the
 * hasura-claims mapper stamps the Auth0-shaped `https://hasura.io/jwt/claims`
 * object into both tokens, so nothing downstream had to change. This module
 * deliberately re-exposes an Auth0-shaped `useAuth()` surface
 * (isAuthenticated / user / isLoading / getAccessTokenSilently /
 * loginWithRedirect / logout) so the existing call sites only swap
 * `useAuth0()` -> `useAuth()`.
 */

const authority = `${process.env.REACT_APP_KEYCLOAK_URL}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}`

const oidcConfig: AuthProviderProps = {
  authority,
  client_id: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
  redirect_uri: window.location.origin + '/callback',
  post_logout_redirect_uri: window.location.origin,
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, '/')
  },
}

export function AppAuthProvider({ children }: { children: React.ReactNode }) {
  return <OidcAuthProvider {...oidcConfig}>{children}</OidcAuthProvider>
}

type AuthUser = {
  email?: string
  [claim: string]: any
}

export type AuthError = Error & {
  error?: string
  error_description?: string
  source?: string
  innerError?: { error?: string; error_description?: string }
}

export function authErrorReason(error?: AuthError): string | undefined {
  if (!error) return undefined
  return (
    error.error_description ??
    error.innerError?.error_description ??
    error.innerError?.error ??
    error.message
  )
}

interface LoginOptions {
  authorizationParams?: { ui_locales?: string }
}

interface LogoutOptions {
  logoutParams?: { returnTo?: string }
}

/** Auth0-shaped hook backed by react-oidc-context. */
export function useAuth() {
  const auth = useOidcAuth()

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user?.profile as AuthUser | undefined,

    error: auth.error as AuthError | undefined,

    getAccessTokenSilently: async (): Promise<string | null> => {
      if (auth.user && !auth.user.expired) return auth.user.access_token
      try {
        const renewed = await auth.signinSilent()
        return renewed?.access_token ?? null
      } catch {
        return auth.user?.access_token ?? null
      }
    },

    loginWithRedirect: (opts?: LoginOptions) => {
      const uiLocales = opts?.authorizationParams?.ui_locales
      return auth.signinRedirect(
        uiLocales ? { extraQueryParams: { ui_locales: uiLocales } } : undefined
      )
    },

    logout: (opts?: LogoutOptions) =>
      auth.signoutRedirect({
        post_logout_redirect_uri:
          opts?.logoutParams?.returnTo ?? window.location.origin,
      }),
  }
}
